<script setup lang="ts">
import { useBillingStore, type BillingTermKind } from '~/store/billing'

/**
 * Storage sizing.
 *
 * Picking a plan used to read as picking a fixed disk: the plan row showed a
 * flat "10 GB" and the only way to change it was a pair of ± buttons seven
 * sections further down, past the OS, the password and the network. This step
 * sits directly under the plan table and leads with a slider, so the disk is
 * visibly a dial rather than a plan attribute — and it prices every GB as the
 * slider moves, from the catalog's own per-GB rate.
 */
const props = withDefaults(
  defineProps<{
    allowMainExpansion?: boolean;
    allowExtraDisks?: boolean;
    /** Disk the plan includes. The slider can never go below it. */
    baseGb: number;
    /** GB granularity every disk control snaps to (catalog). */
    stepGb: number;
    /** Smallest additional data disk. */
    minExtraGb: number;
    /** Main-disk expansion forced by the chosen OS (0 when none). */
    minMainExtraGb: number;
    /** Chosen OS + its own disk floor, for the "why did it jump" note. */
    osName?: string | null;
    osMinDiskGb?: number | null;
    mainExtraGb: number;
    extraDisks: { size_gb: number }[];
    term: BillingTermKind;
  }>(),
  { allowMainExpansion: true, allowExtraDisks: true },
);

const emit = defineEmits<{
  'update:mainExtraGb': [number]
  'update:extraDisks': [{ size_gb: number }[]]
}>()

const billing = useBillingStore()
const pricing = useCheckoutPricing()

/**
 * Storage is a BUDGET, not a per-disk limit: the catalog publishes one ceiling
 * for the whole instance (`catalog_addons.max_quantity` on the storage-config
 * addon, editable at /catalog) and the customer splits it across the main disk
 * and any additional disks however they like. So every control's headroom is
 * the ceiling minus what the *other* disks already claim.
 *
 * Null means no ceiling is configured — the UI must not invent one, so tracks
 * fall back to a range worth dragging through and the ± steppers stay open,
 * exactly as before this field existed.
 */
const UNCAPPED_TRACK_GB = 1000
const maxTotalStorageGb = computed(
  () => billing.catalog?.storage?.max_total_storage_gb ?? null
)

const mainTotalGb = computed(() => props.baseGb + props.mainExtraGb)
const extraDisksTotalGb = computed(() =>
  props.extraDisks.reduce((sum, disk) => sum + disk.size_gb, 0)
)
const totalStorageGb = computed(() => mainTotalGb.value + extraDisksTotalGb.value)
const remainingGb = computed(() =>
  maxTotalStorageGb.value == null
    ? null
    : Math.max(0, maxTotalStorageGb.value - totalStorageGb.value)
)

/**
 * Track end for a control sitting at `currentGb` while `claimedByOthersGb` is
 * spoken for elsewhere. Without a ceiling the track grows to follow a value the
 * stepper pushed past the default range, so dragging never snaps a disk down.
 */
function trackMax(currentGb: number, claimedByOthersGb: number) {
  const ceiling = maxTotalStorageGb.value
  if (ceiling == null) return Math.max(UNCAPPED_TRACK_GB, currentGb)
  // Never below the current value: an over-budget config (a ceiling lowered
  // after the fact) must stay draggable downwards rather than jump.
  return Math.max(ceiling - claimedByOthersGb, currentGb)
}

const minMainTotalGb = computed(() => props.baseGb + props.minMainExtraGb)
const sliderMax = computed(() =>
  Math.max(
    trackMax(mainTotalGb.value, extraDisksTotalGb.value),
    props.baseGb + props.stepGb
  )
)

/** Round sizes that cover most orders, filtered to what this plan allows. */
const presets = computed(() => {
  const candidates = [props.baseGb, 50, 100, 250, 500, 1000, 2000]
  const seen = new Set<number>()
  return candidates
    .filter((gb) => {
      if (gb < minMainTotalGb.value || gb > sliderMax.value) return false
      if (gb % props.stepGb !== 0) return false
      if (seen.has(gb)) return false
      seen.add(gb)
      return true
    })
    .sort((a, b) => a - b)
})

function setMainTotal(totalGb: number) {
  const snapped = Math.round(totalGb / props.stepGb) * props.stepGb
  // The OS minimum outranks the budget: a ceiling that cannot host the chosen
  // OS is a misconfiguration, and the server's own check is the backstop.
  const clamped = Math.max(
    minMainTotalGb.value,
    Math.min(snapped, sliderMax.value)
  )
  emit('update:mainExtraGb', clamped - props.baseGb)
}

const mainSliderValue = computed({
  get: () => mainTotalGb.value,
  set: (value: number) => setMainTotal(value),
})

// ── Live price of what the sliders are doing ──────────────────────────────
const isOnDemand = computed(() => props.term === 'ON_DEMAND')
const mainRate = computed(
  () => billing.catalog?.storage?.main_disk_rates_by_term?.[props.term] ?? null
)
const extraRate = computed(
  () => billing.catalog?.storage?.extra_disk_rates_by_term?.[props.term] ?? null
)

/**
 * What `gb` extra gigabytes add to the bill, in the same unit the rest of the
 * checkout quotes: per month for a commitment, per hour on-demand (which has no
 * month to speak of). Null when the catalog omits the rate — better to show
 * nothing than a wrong number the simulation will contradict.
 */
function priceForGb(gb: number, rateMicro: string | null): string | null {
  if (!rateMicro || gb <= 0) return null
  try {
    const perHour = BigInt(rateMicro) * BigInt(gb)
    return isOnDemand.value
      ? `${billing.formatMicroCents(perHour.toString())}/h`
      : billing.formatMicroCents((perHour * BigInt(pricing.monthHours.value)).toString())
  } catch {
    return null
  }
}

const mainExtraPrice = computed(() =>
  priceForGb(props.mainExtraGb, mainRate.value)
)

const extraDisksPrice = computed(() =>
  priceForGb(extraDisksTotalGb.value, extraRate.value)
)

/** Everything storage adds beyond what the plan includes, priced together. */
const totalStoragePrice = computed(() => {
  if (!mainRate.value || !extraRate.value) return null
  try {
    const perHour =
      BigInt(mainRate.value) * BigInt(props.mainExtraGb) +
      BigInt(extraRate.value) * BigInt(extraDisksTotalGb.value)
    if (perHour <= 0n) return null
    return isOnDemand.value
      ? `${billing.formatMicroCents(perHour.toString())}/h`
      : billing.formatMicroCents(
          (perHour * BigInt(pricing.monthHours.value)).toString()
        )
  } catch {
    return null
  }
})

// ── Additional data disks ─────────────────────────────────────────────────
/** GB every disk other than `index` already claims (main disk included). */
function claimedByOthers(index: number) {
  return (
    mainTotalGb.value +
    props.extraDisks.reduce(
      (sum, disk, i) => (i === index ? sum : sum + disk.size_gb),
      0
    )
  )
}
/** Track end for extra disk `index`, given the rest of the budget. */
function diskTrackMax(index: number) {
  return trackMax(props.extraDisks[index]?.size_gb ?? 0, claimedByOthers(index))
}
/** A new disk needs at least the minimum increment of unclaimed budget. */
const canAddDisk = computed(
  () => remainingGb.value == null || remainingGb.value >= props.minExtraGb
)

function addDisk() {
  if (!canAddDisk.value) return
  emit('update:extraDisks', [
    ...props.extraDisks,
    { size_gb: props.minExtraGb },
  ])
}
function setDiskSize(index: number, sizeGb: number) {
  const snapped = Math.round(sizeGb / props.stepGb) * props.stepGb
  const clamped = Math.max(
    props.minExtraGb,
    Math.min(snapped, diskTrackMax(index))
  )
  const next = props.extraDisks.map((disk, i) =>
    i === index ? { size_gb: clamped } : disk
  )
  emit('update:extraDisks', next)
}
function removeDisk(index: number) {
  emit(
    'update:extraDisks',
    props.extraDisks.filter((_, i) => i !== index)
  )
}

/**
 * The chosen OS imposes a floor on this disk.
 *
 * This states the CONSTRAINT and nothing else. It used to say "we raised the
 * disk" — persistently, whenever a floor existed, including when the customer
 * had sized the disk themselves and nothing had been adjusted at all. Saying
 * what CHANGED is now `autoAdjustments`, which fires only when the checkout
 * actually moved something and names what it moved from, to, and what it cost.
 */
const osMinimum = computed(
  () => props.minMainExtraGb > 0 && !!props.osName && !!props.osMinDiskGb
)
</script>

<template>
  <div class="space-y-5">
    <!-- ── Main disk ────────────────────────────────────────────────── -->
    <div class="space-y-3">
      <div class="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div class="text-sm font-medium flex items-center gap-1.5">
            <UIcon name="i-lucide-hard-drive" class="size-4 text-primary" />
            {{ $t('billing.page.checkout.disks.mainDisk') }}
          </div>
          <p class="text-xs text-muted mt-0.5">
            {{
              allowMainExpansion
                ? $t("billing.page.checkout.disks.mainDiskIncluded", {
                    base: baseGb,
                  })
                : $t("trial.includedDisk", { base: baseGb })
            }}
          </p>
        </div>
        <div class="text-right">
          <div class="text-2xl font-bold tabular-nums leading-none">
            {{ mainTotalGb }} <span class="text-base font-medium text-muted">GB</span>
          </div>
          <div class="text-xs mt-1">
            <span v-if="mainExtraPrice" class="text-primary font-medium">
              +{{ mainExtraPrice
              }}<span v-if="!isOnDemand" class="text-muted font-normal">{{
                $t('billing.page.checkout.term.perMonth')
              }}</span>
            </span>
            <span v-else class="text-muted">
              {{ $t('billing.page.checkout.disks.includedInPlan') }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="allowMainExpansion" class="flex items-center gap-3">
        <USlider
          v-model="mainSliderValue"
          class="flex-1"
          :min="minMainTotalGb"
          :max="sliderMax"
          :step="stepGb"
          :aria-label="$t('billing.page.checkout.disks.mainDisk')"
        />
        <div class="flex items-center gap-1 shrink-0">
          <UButton
            size="xs"
            variant="outline"
            icon="i-lucide-minus"
            :aria-label="$t('billing.page.checkout.disks.decrease')"
            :disabled="mainTotalGb <= minMainTotalGb"
            @click="setMainTotal(mainTotalGb - stepGb)"
          />
          <UButton
            size="xs"
            variant="outline"
            icon="i-lucide-plus"
            :aria-label="$t('billing.page.checkout.disks.increase')"
            :disabled="remainingGb != null && remainingGb < stepGb"
            @click="setMainTotal(mainTotalGb + stepGb)"
          />
        </div>
      </div>

      <div
        v-if="allowMainExpansion"
        class="flex items-center gap-1.5 flex-wrap"
      >
        <span class="text-xs text-muted mr-1">
          {{ $t('billing.page.checkout.disks.quickSizes') }}
        </span>
        <UButton
          v-for="preset in presets"
          :key="preset"
          size="xs"
          :variant="mainTotalGb === preset ? 'solid' : 'soft'"
          :color="mainTotalGb === preset ? 'primary' : 'neutral'"
          @click="setMainTotal(preset)"
        >
          {{ preset }} GB
        </UButton>
      </div>

      <p v-if="osMinimum" class="text-xs text-toned flex items-start gap-1.5">
        <UIcon name="i-lucide-info" class="size-3.5 shrink-0 mt-px" aria-hidden="true" />
        {{
          $t('billing.page.checkout.disks.osMinimumNote', {
            name: osName,
            gb: osMinDiskGb,
          })
        }}
      </p>
    </div>

    <!-- ── Additional data disks ────────────────────────────────────── -->
    <div v-if="allowExtraDisks" class="space-y-3 pt-4 border-t border-default">
      <div class="flex items-start justify-between gap-3">
        <div>
          <div class="text-sm font-medium">
            {{ $t('billing.page.checkout.disks.additionalDisks') }}
          </div>
          <p class="text-xs text-muted mt-0.5">
            {{ $t('billing.page.checkout.disks.additionalDisksHint') }}
          </p>
        </div>
        <UButton
          size="xs"
          variant="outline"
          icon="i-lucide-plus"
          :label="$t('billing.page.checkout.disks.addDisk')"
          :disabled="!canAddDisk"
          @click="addDisk"
        />
      </div>

      <div v-if="extraDisks.length > 0" class="space-y-3">
        <div
          v-for="(disk, index) in extraDisks"
          :key="index"
          class="rounded-lg border border-default p-3 space-y-2"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-medium">
              {{ $t('billing.page.checkout.disks.diskLabel', { n: index + 1 }) }}
            </span>
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold tabular-nums">{{ disk.size_gb }} GB</span>
              <UButton
                size="xs"
                color="error"
                variant="ghost"
                icon="i-lucide-trash-2"
                :aria-label="$t('billing.page.checkout.disks.removeDisk')"
                @click="removeDisk(index)"
              />
            </div>
          </div>
          <div class="flex items-center gap-3">
            <USlider
              :model-value="disk.size_gb"
              class="flex-1"
              :min="minExtraGb"
              :max="diskTrackMax(index)"
              :step="stepGb"
              :aria-label="$t('billing.page.checkout.disks.diskLabel', { n: index + 1 })"
              @update:model-value="setDiskSize(index, Number($event))"
            />
            <div class="flex items-center gap-1 shrink-0">
              <UButton
                size="xs"
                variant="outline"
                icon="i-lucide-minus"
                :aria-label="$t('billing.page.checkout.disks.decrease')"
                :disabled="disk.size_gb <= minExtraGb"
                @click="setDiskSize(index, disk.size_gb - stepGb)"
              />
              <UButton
                size="xs"
                variant="outline"
                icon="i-lucide-plus"
                :aria-label="$t('billing.page.checkout.disks.increase')"
                :disabled="remainingGb != null && remainingGb < stepGb"
                @click="setDiskSize(index, disk.size_gb + stepGb)"
              />
            </div>
          </div>
        </div>
        <p v-if="extraDisksPrice" class="text-xs text-muted text-right">
          {{
            $t('billing.page.checkout.disks.extraDisksPrice', {
              gb: extraDisksTotalGb,
              price: extraDisksPrice,
            })
          }}<span v-if="!isOnDemand">{{ $t('billing.page.checkout.term.perMonth') }}</span>
        </p>
      </div>
    </div>

    <!-- ── Roll-up ──────────────────────────────────────────────────────
         With a ceiling configured this is a budget, so it has to show the
         budget: every slider's headroom is the ceiling minus what the other
         disks take, and a shrinking maximum is baffling without the number
         it is shrinking against. -->
    <div class="rounded-lg bg-elevated/50 px-3 py-2.5 space-y-2">
      <div class="flex items-center justify-between gap-3">
        <span class="text-sm text-muted">
          {{ $t('billing.page.checkout.disks.totalStorageLabel') }}
        </span>
        <span class="text-sm font-semibold tabular-nums">
          {{ totalStorageGb }} GB<span
            v-if="maxTotalStorageGb"
            class="text-muted font-normal"
            >{{
              $t('billing.page.checkout.disks.ofMaxTotal', {
                max: maxTotalStorageGb,
              })
            }}</span
          >
          <span v-if="totalStoragePrice" class="text-primary font-medium">
            · +{{ totalStoragePrice
            }}<span v-if="!isOnDemand" class="text-muted font-normal">{{
              $t('billing.page.checkout.term.perMonth')
            }}</span>
          </span>
        </span>
      </div>
      <template v-if="maxTotalStorageGb">
        <UProgress
          size="xs"
          :model-value="Math.min(100, (totalStorageGb / maxTotalStorageGb) * 100)"
          :color="remainingGb === 0 ? 'warning' : 'primary'"
        />
        <p class="text-[11px] text-muted">
          <template v-if="remainingGb === 0">
            {{ $t('billing.page.checkout.disks.limitReached', { max: maxTotalStorageGb }) }}
          </template>
          <template v-else>
            {{ $t('billing.page.checkout.disks.remaining', { gb: remainingGb }) }}
          </template>
        </p>
      </template>
    </div>
  </div>
</template>
